import type React from 'react';

import type { Text } from '@zillow/constellation';
import {
  AdornedInput,
  Adornment,
  FieldSet,
  FormField,
  FormHelp,
  InlineFeedback,
  Input,
  Label,
  LabeledControl,
  Legend,
  Paragraph,
  Radio,
  Spacer,
  Textarea,
} from '@zillow/constellation';
import { CurrencyInput } from '@zillow/zrs-comet';

import type { Section } from '../../../../models';
import { WATER_BILLING } from '../../../../constants';
import type { HTMLElementEvent } from '../../../../models/Types';
import { CURRENCY_INPUT_PLACEHOLDER } from '../../../../constants/stringConstants';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
  children?: React.ReactNode;
};

function WaterBilling({ section, setSectionValues, children }: Props) {
  const averageComparative = 'averageComparative';
  const averageFamily = 'averageFamily';

  function onEstimatedMonthlyChargeChange(event: HTMLElementEvent<typeof Text>): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [WATER_BILLING.FIELD_NAME.ESTIMATED_MONTHLY_CHARGE]: value,
    });
  }

  function onEstimatedMonthlyChargeSelection(event: any): void {
    const { id } = event.currentTarget;

    setSectionValues({
      [WATER_BILLING.FIELD_NAME.AVERAGE_COMPARATIVE]: id === averageComparative,
      [WATER_BILLING.FIELD_NAME.AVERAGE_FAMILY]: id === averageFamily,
    });
  }

  function onPhoneNumberChange(event: HTMLElementEvent<typeof Text>) {
    const { value } = event.currentTarget;

    setSectionValues({
      [WATER_BILLING.FIELD_NAME.PHONE_NUMBER]: value,
    });
  }

  function onMailingAddressChange(event: HTMLElementEvent<typeof Text>) {
    const { value } = event.currentTarget;

    setSectionValues({
      [WATER_BILLING.FIELD_NAME.MAILING_ADDRESS]: value,
    });
  }

  function onEmailAddressChange(event: HTMLElementEvent<typeof Text>) {
    const { value } = event.currentTarget;

    setSectionValues({
      [WATER_BILLING.FIELD_NAME.EMAIL_ADDRESS]: value,
    });
  }

  function onTimeAndDatesChange(event: HTMLElementEvent<typeof Text>) {
    const { value } = event.currentTarget;

    setSectionValues({
      [WATER_BILLING.FIELD_NAME.TIME_AND_DATES]: value,
    });
  }

  function onWaterDueDateChange(event: HTMLElementEvent<typeof Text>) {
    const { value } = event.currentTarget;

    setSectionValues({
      [WATER_BILLING.FIELD_NAME.WATER_DUE_DATE]: value,
    });
  }

  function onWaterPaymentProcedureChange(event: HTMLElementEvent<typeof Text>) {
    const { value } = event.currentTarget;

    setSectionValues({
      [WATER_BILLING.FIELD_NAME.WATER_PAYMENT_PROCEDURES]: value,
    });
  }

  return (
    <>
      <Paragraph fontType="h6">Water/sewer</Paragraph>
      <FormField>
        <Label>Estimated water bill</Label>
        <AdornedInput
          leftAdornment={<Adornment>$</Adornment>}
          rightAdornment={<Adornment>per month</Adornment>}
          input={
            <CurrencyInput
              aria-labelledby="estimated-water-bill"
              placeholder={CURRENCY_INPUT_PLACEHOLDER}
              value={section.getFieldValue(WATER_BILLING.FIELD_NAME.ESTIMATED_MONTHLY_CHARGE)}
              onChange={onEstimatedMonthlyChargeChange}
              onBlur={onEstimatedMonthlyChargeChange}
            />
          }
        />
      </FormField>
      <Spacer marginTop="sm" />
      <FieldSet legend={<Legend>How did you estimate the water bill?</Legend>}>
        <LabeledControl
          controlId={averageComparative}
          label={
            <Label>
              The average or median bill for water service for comparative dwelling units at the property over any three
              of the past six months
            </Label>
          }
          control={
            <Radio
              name="water-billing"
              checked={section.getFieldValue(WATER_BILLING.FIELD_NAME.AVERAGE_COMPARATIVE)}
              onChange={onEstimatedMonthlyChargeSelection}
            />
          }
        />
        <LabeledControl
          controlId={averageFamily}
          label={
            <Label>
              The average indoor water use of a family of four of approximately 200 gallons per day, and including all
              other monthly charges that will be assessed
            </Label>
          }
          control={
            <Radio
              name="water-billing"
              checked={section.getFieldValue(WATER_BILLING.FIELD_NAME.AVERAGE_FAMILY)}
              onChange={onEstimatedMonthlyChargeSelection}
            />
          }
        />
      </FieldSet>
      <FormField marginY="sm">
        <Label>When is the water bill due date?</Label>
        <Input
          aria-labelledby="water-due-date"
          placeholder="(example: First of the month)"
          value={section.getFieldValue(WATER_BILLING.FIELD_NAME.WATER_DUE_DATE)}
          onChange={onWaterDueDateChange}
        />
      </FormField>
      <FormField marginBottom="sm">
        <Label>What are the payment procedures for the water bill?</Label>
        <Textarea
          aria-labelledby="water-payment-procedures"
          resize="vertical"
          value={section.getFieldValue(WATER_BILLING.FIELD_NAME.WATER_PAYMENT_PROCEDURES)}
          onChange={onWaterPaymentProcedureChange}
        />
      </FormField>
      {children}
      <InlineFeedback marginBottom="sm">
        The tenant may contact you with questions about water billing with the information provided below
      </InlineFeedback>
      <FormField marginBottom="sm">
        <Label>Mailing address</Label>
        <Textarea
          aria-labelledby="water-billing-mailing-address"
          resize="vertical"
          value={section.getFieldValue(WATER_BILLING.FIELD_NAME.MAILING_ADDRESS)}
          onChange={onMailingAddressChange}
        />
        <FormHelp>Street, city, state, ZIP Code</FormHelp>
      </FormField>
      <FormField marginBottom="sm">
        <Label>Email address</Label>
        <Input
          aria-labelledby="water-billing-email-address"
          value={section.getFieldValue(WATER_BILLING.FIELD_NAME.EMAIL_ADDRESS)}
          onChange={onEmailAddressChange}
          type="email"
        />
        <FormHelp>This can either be the utility’s email or a personal email</FormHelp>
      </FormField>
      <FormField marginBottom="sm">
        <Label>Phone number</Label>
        <Input
          aria-labelledby="water-billing-phone-number"
          type="tel"
          data-field-name={section.getField(WATER_BILLING.FIELD_NAME.PHONE_NUMBER).fieldName}
          value={section.getFieldValue(WATER_BILLING.FIELD_NAME.PHONE_NUMBER)}
          error={section
            .getField(WATER_BILLING.FIELD_NAME.PHONE_NUMBER)
            .showOnError(WATER_BILLING.ERROR_MESSAGE.PHONE_NUMBER)}
          onChange={onPhoneNumberChange}
        />
        <FormHelp>Local or toll-free numbers only</FormHelp>
      </FormField>
      <FormField marginBottom="sm">
        <Label>During what times and days can the tenant call you?</Label>
        <Textarea
          aria-labelledby="water-billing-schedule"
          resize="vertical"
          value={section.getFieldValue(WATER_BILLING.FIELD_NAME.TIME_AND_DATES)}
          onChange={onTimeAndDatesChange}
        />
        <FormHelp>Include timezones if you live in a different timezone than where the property is located</FormHelp>
      </FormField>
    </>
  );
}

WaterBilling.description = 'Asks if the unit has a water submeter';

export default WaterBilling;
